import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useNotification } from '../context/NotificationContext';
import { useTheme } from '../context/DarkModeContext';
import { getUserThreads, subscribeToUserThreads, getProfile, subscribeToThread, deleteThread } from '../utils/supabase/index';
import { HiPlus, HiUser, HiTrash, HiX, HiCheck, HiSelector, HiPencil, HiChat, HiRefresh } from 'react-icons/hi';
import { DiceBearAvatar } from './DiceBearAvatar';
import type { Database } from '../types/supabase';
import { supabase } from '../utils/supabase/index';
import { checkAndDeleteExpiredMessages } from '../utils/supabase/autoDelete';

type Thread = Database['public']['Tables']['threads']['Row'] & {
  messages: Database['public']['Tables']['messages']['Row'][];
  otherParticipant?: {
    username: string;
    avatar_url: string | null;
    avatar_seed?: string | null;
  };
};

// Define the Message type
type Message = Database['public']['Tables']['messages']['Row'];

export const ChatList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { isNaturalTheme } = useTheme();
  const { 
    notificationsEnabled, 
    showNotification, 
    incrementUnread,
    playNotificationSound 
  } = useNotification();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Keep track of threads with unread messages
  const [unreadThreads, setUnreadThreads] = useState<Record<string, number>>({});
  // Keep track of our own subscriptions for cleanup
  const messageUnsubscribesRef = useRef<(() => void)[]>([]);
  // State for delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Multi-select functionality
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedThreads, setSelectedThreads] = useState<string[]>([]);
  // Display wallet addresses in shortened form
  const [expandedThread, setExpandedThread] = useState<string | null>(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);

  // Filter threads based on search query
  const filteredThreads = threads.filter(thread => 
    thread.otherParticipant?.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getLastMessage(thread).toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (user) {
      loadThreadsWithParticipants();
      
      // Subscribe to new threads
      const userThreadUnsubscribe = subscribeToUserThreads(user.id, (payload) => {
        console.log('New thread notification received:', payload);
        if (payload.new) {
          // Refresh threads list to include the new thread
          loadThreadsWithParticipants();
          
          // Show notification if the thread was created by someone else
          if (payload.new.created_by !== user.id) {
            // Use enhanced notification system
            if (notificationsEnabled && !document.hasFocus()) {
              showNotification('New Chat', {
                body: `New chat: ${payload.new.name}`,
                data: {
                  threadId: payload.new.id,
                  url: `/app/chat/${payload.new.id}`
                },
                tag: `thread-${payload.new.id}`,
                senderId: payload.new.created_by // Add sender ID to prevent notification for our own messages
              } as any); // Cast to any to allow our custom property
              
              // Increment unread counter
              incrementUnread();
              
              // Update unread threads state
              setUnreadThreads(prev => ({
                ...prev,
                [payload.new.id]: (prev[payload.new.id] || 0) + 1
              }));
              
              // Play notification sound for new chat if we didn't create it
              if (payload.new.created_by !== user.id) {
                playNotificationSound();
              }
            }
          }
        }
      });

      // Setup subscriptions for existing threads
      const setupThreadSubscriptions = async () => {
        if (threads.length > 0) {
          // Clean up any previous message subscriptions
          messageUnsubscribesRef.current.forEach(unsub => unsub());
          messageUnsubscribesRef.current = [];
          
          threads.forEach(thread => {
            // Add subscription for each thread to track new messages
            const unsubscribe = subscribeToThread(
              thread.id, 
              (payload: { new: Message; old: Message | null }) => {
                if (payload.new && payload.new.sender_id !== user.id) {
                  console.log('New message in thread:', thread.id, payload.new);
                  
                  // Update unread count for this thread
                  setUnreadThreads(prev => ({
                    ...prev,
                    [thread.id]: (prev[thread.id] || 0) + 1
                  }));
                  
                  // Also refresh the threads to update last_message timestamp
                  loadThreadsWithParticipants();
                  
                  // Show visual notification if not in focus and notifications are enabled
                  if (notificationsEnabled && !document.hasFocus()) {
                    const senderName = thread.otherParticipant?.username || 'Someone';
                    
                    // Only send notification if the current URL doesn't include this thread ID
                    // This prevents notifications when already in the chat
                    const currentThreadId = window.location.pathname.split('/').pop();
                    if (currentThreadId !== thread.id) {
                      showNotification(`New message from ${senderName}`, {
                        body: payload.new.content,
                        data: {
                          threadId: thread.id,
                          url: `/app/chat/${thread.id}`
                        },
                        tag: `thread-${thread.id}`,
                        senderId: payload.new.sender_id // Add sender ID to prevent notification for our own messages
                      } as any); // Cast to any to allow our custom property
                      
                      // Increment global unread counter
                      incrementUnread();
                      
                      // Make sure the sound plays for new messages if we have notification permissions
                      if (Notification.permission === 'granted') {
                        playNotificationSound();
                      }
                    }
                  }
                }
              },
              (updatePayload) => {
                // Handle thread updates
                if (updatePayload && updatePayload.new) {
                  console.log(`Thread ${thread.id} updated:`, updatePayload);
                  
                  // Refresh threads to get updated data like last_message_at
                  loadThreadsWithParticipants();
                }
              }
            );
            
            // Track this subscription for cleanup
            messageUnsubscribesRef.current.push(unsubscribe);
          });
        }
      };
      
      // Setup subscriptions when threads are loaded
      if (threads.length > 0) {
        setupThreadSubscriptions();
      }

      return () => {
        if (userThreadUnsubscribe) {
          userThreadUnsubscribe();
        }
        
        // Clean up all message subscriptions
        messageUnsubscribesRef.current.forEach(unsub => {
          unsub();
        });
        messageUnsubscribesRef.current = [];
      };
    }
  }, [user, notificationsEnabled, showNotification, incrementUnread, playNotificationSound, threads.length]);

  // Add an interval to periodically refresh unread counts - this ensures they stay up to date
  useEffect(() => {
    if (user) {
      // Refresh unread counts every 30 seconds
      const intervalId = setInterval(() => {
        refreshUnreadCounts();
      }, 30000); // 30 seconds
      
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [user]);
  
  // Subscribe to profile changes to update avatars in real-time
  useEffect(() => {
    if (!user) return;
    
    // Create a subscription to listen for profile updates
    const subscription = supabase
      .channel('chatlist-profile-updates')
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'profiles'
        }, 
          async (payload) => {
            console.log(`Profile updated, refreshing threads to update avatars`);
            
            try {
              // Get the updated profile ID from the payload
              const profileId = payload.new?.id;
              if (!profileId) {
                console.error('No profile ID in payload:', payload);
                return;
              }
              
              // Fetch the updated profile
              const updatedProfile = await getProfile(profileId);
              if (!updatedProfile) {
                console.error('Could not fetch updated profile for ID:', profileId);
                return;
              }
              
              console.log(`Fetched updated profile for ${profileId}:`, {
                avatar_url: updatedProfile.avatar_url,
                avatar_seed: updatedProfile.avatar_seed
              });
              
              // Force a full reload of threads to get updated avatar information
              await loadThreadsWithParticipants();
            } catch (error) {
              console.error('Error handling profile update:', error);
            }
          }
      )
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, [user]);
  
  // Function to refresh unread counts without loading all thread data
  const refreshUnreadCounts = async () => {
    if (!user) return;
    
    try {
      // For each thread, count unread messages
      const unreadCounters: Record<string, number> = {};
      
      await Promise.all(threads.map(async (thread: Thread) => {
        try {
          // Fetch messages for this thread
          const { data: messagesData } = await supabase
            .from('messages')
            .select('*')
            .eq('thread_id', thread.id)
            .eq('read', false)
            .neq('sender_id', user.id); // Only count messages not sent by current user
            
          // Set unread count for this thread
          if (messagesData && messagesData.length > 0) {
            unreadCounters[thread.id] = messagesData.length;
          }
        } catch (error) {
          console.error(`Error counting unread messages for thread ${thread.id}:`, error);
        }
      }));
      
      // Update unread threads state with the latest counts
      setUnreadThreads(unreadCounters);
      console.log('Unread counts refreshed:', unreadCounters);
    } catch (error) {
      console.error('Error refreshing unread counts:', error);
    }
  };

  const loadThreadsWithParticipants = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!user) {
        setLoading(false);
        return;
      }

      // Check for expired messages before loading threads
      if (user.id) {
        try {
          const deletedCount = await checkAndDeleteExpiredMessages(user.id);
          if (deletedCount > 0) {
            console.log(`Auto-deleted ${deletedCount} expired messages`);
          }
        } catch (error) {
          console.error('Error checking for expired messages:', error);
        }
      }
      
      // Get threads with participant details
      const threads = await getUserThreads(user.id);
      
      // Load participant profiles for each thread
      const threadsWithParticipants = await Promise.all(
        threads.map(async (thread) => {
          const otherParticipantId = thread.participant_ids.find((id: string) => id !== user.id);
          
          if (otherParticipantId) {
            try {
              const profile = await getProfile(otherParticipantId);
              return {
                ...thread,
                otherParticipant: profile ? {
                  username: profile.username || 'Unknown User',
                  avatar_url: profile.avatar_url || null,
                  avatar_seed: profile.avatar_seed || null
                } : {
                  username: 'Unknown User',
                  avatar_url: null,
                  avatar_seed: null
                }
              };
            } catch (error) {
              console.error(`Error loading profile for ${otherParticipantId}:`, error);
              return {
                ...thread,
                otherParticipant: {
                  username: 'Unknown User',
                  avatar_url: null,
                  avatar_seed: null
                }
              };
            }
          }
          return thread;
        })
      );
      
      setThreads(threadsWithParticipants);

      // Initialize unread counters for each thread
      const unreadCounters: Record<string, number> = {};
      
      // For each thread, count unread messages
      await Promise.all(threadsWithParticipants.map(async (thread: Thread) => {
        try {
          // Fetch messages for this thread
          const { data: messagesData } = await supabase
            .from('messages')
            .select('*')
            .eq('thread_id', thread.id)
            .eq('read', false)
            .neq('sender_id', user.id); // Only count messages not sent by current user
            
          // Set unread count for this thread
          if (messagesData && messagesData.length > 0) {
            unreadCounters[thread.id] = messagesData.length;
          }
        } catch (error) {
          console.error(`Error counting unread messages for thread ${thread.id}:`, error);
        }
      }));
      
      // Update unread threads state with the initial counts
      setUnreadThreads(unreadCounters);
    } catch (error) {
      console.error('Error loading threads:', error);
      setError('Failed to load chats');
    } finally {
      setLoading(false);
    }
  };

  const getLastMessage = (thread: Thread) => {
    if (!thread.messages || thread.messages.length === 0) {
      return 'No messages yet';
    }
    // Truncate long messages for the preview
    const message = thread.messages[0].content;
    // Return a shorter preview for mobile
    return message.length > 30 ? message.substring(0, 27) + '...' : message;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return `${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (days === 1) {
      return `Yesterday`;
    } else if (days < 7) {
      return `${date.toLocaleDateString([], { weekday: 'short' })}`;
    } else {
      return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
    }
  };
  
  // Shorten wallet address for display
  const shortenAddress = (address: string) => {
    if (!address) return '';
    if (address.length <= 15) return address;
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Toggle selection mode
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      // If turning off selection mode, clear selections
      setSelectedThreads([]);
    }
  };

  // Select all threads
  const selectAllThreads = () => {
    if (selectedThreads.length === filteredThreads.length) {
      // If all are selected, deselect all
      setSelectedThreads([]);
    } else {
      // Otherwise, select all
      setSelectedThreads(filteredThreads.map(thread => thread.id));
    }
  };

  // Toggle thread selection
  const toggleThreadSelection = (threadId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigating to the chat
    
    if (selectedThreads.includes(threadId)) {
      setSelectedThreads(selectedThreads.filter(id => id !== threadId));
    } else {
      setSelectedThreads([...selectedThreads, threadId]);
    }
  };

  // Toggle expanded view for a thread (to see full wallet address)
  const toggleExpandThread = (threadId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigating to the chat
    if (expandedThread === threadId) {
      setExpandedThread(null);
    } else {
      setExpandedThread(threadId);
    }
  };

  // Handle opening the delete confirmation dialog
  const handleOpenDeleteDialog = (threadId: string | null, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent navigating to the chat
    }
    
    setThreadToDelete(threadId);
    setShowDeleteDialog(true);
    setDeleteError(null);
  };

  // Open delete dialog for multiple chats
  const openMultiDeleteDialog = () => {
    if (selectedThreads.length === 0) return;
    setShowDeleteDialog(true);
    setDeleteError(null);
  };

  // Handle the deletion of a thread
  const handleDeleteThread = async () => {
    if (!user) return;
    
    setDeleteLoading(true);
    setDeleteError(null);
    
    try {
      // Determine which threads to delete
      const threadsToDelete = threadToDelete 
        ? [threadToDelete] 
        : selectedThreads;
      // Show explosion for the first thread (single delete UI)
      if (threadsToDelete.length > 0) {
        setDeletingThreadId(threadsToDelete[0]);
        await new Promise(res => setTimeout(res, 600)); // Wait for animation
      }
      // Delete each thread
      for (const threadId of threadsToDelete) {
        await deleteThread(threadId, user.id);
      }
      
      // Reload threads to ensure UI is up to date
      await loadThreadsWithParticipants();
      
      // Clear selections
      setSelectedThreads([]);
      
      // Exit selection mode if no threads left selected
      if (selectedThreads.length === threadsToDelete.length) {
        setSelectionMode(false);
      }
      
      // Close the dialog
      setShowDeleteDialog(false);
      setThreadToDelete(null);
    } catch (error) {
      console.error('Error deleting thread(s):', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete chat(s)');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Close the delete dialog
  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setThreadToDelete(null);
    setDeleteError(null);
  };

  const handleNewChat = () => {
    navigate('/app/chat/new');
  };

  // Listen for app-refresh events
  useEffect(() => {
    const handleAppRefresh = (event: Event) => {
      const customEvent = event as CustomEvent;
      const path = customEvent.detail?.path;
      
      // Only refresh if we're on the chats page or a specific chat page
      if (path && (path === '/app/chats' || path.startsWith('/app/chat/'))) {
        console.log('Refreshing chat list due to refresh event');
        loadThreadsWithParticipants();
      }
    };
    
    // Add event listener
    window.addEventListener('app-refresh', handleAppRefresh);
    
    // Cleanup
    return () => {
      window.removeEventListener('app-refresh', handleAppRefresh);
    };
  }, []);  // Note: This does create a lint warning about missing loadThreadsWithParticipants dependency

  // Helper function to get appropriate hover classes based on theme
  const getHoverClasses = () => {
    if (isNaturalTheme) {
      // Return brown hover effect for natural theme
      return 'hover:bg-amber-50/70 dark:hover:bg-amber-900/30';
    } else {
      // Return green hover effect for default theme
      return 'hover:bg-green-50 dark:hover:bg-green-900/30';
    }
  };

  // Helper function to get the active/selected classes based on theme
  const getActiveClasses = () => {
    if (isNaturalTheme) {
      return 'bg-natural-button-active-bg dark:bg-natural-button-active-bg-dark';
    } else {
      return 'bg-green-100 dark:bg-green-900/50';
    }
  };

  // Add focus/visibility refresh for chat list
  useEffect(() => {
    const refreshOnFocus = () => {
      loadThreadsWithParticipants();
    };
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        refreshOnFocus();
      }
    });
    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [loadThreadsWithParticipants]);

  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => setLoadingTimeout(true), 15000); // 15 seconds
      return () => clearTimeout(timeout);
    } else {
      setLoadingTimeout(false);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#f0f2f5] dark:bg-gray-900">
        <div className="text-gray-600 dark:text-gray-400">
          Loading chats...
          {loadingTimeout && (
            <div className="mt-2 text-red-700 text-sm">
              Still loading? <button className="underline" onClick={() => window.location.reload()}>Reload</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-100 dark:bg-gray-900 natural-light:bg-natural-background natural-dark:bg-natural-dark-background">
      {/* Header */}
      <div className="bg-brand-primary natural-light:bg-natural-primary natural-dark:bg-natural-dark-primary text-white px-4 py-[16px] flex items-center justify-between shadow-md z-10">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center">
            <HiChat size={24} />
          </div>
          <div>
            <div className="flex items-center space-x-4">
              <div className="font-semibold">Chats</div>
            </div>
          </div>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="ml-2 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          aria-label="Hard Refresh"
          title="Hard Refresh"
        >
          <HiRefresh className="" size={20} />
        </button>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-3">
          <div className="px-3 py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base leading-6 font-medium text-gray-900 dark:text-white">
                Search Chats
              </h3>
              {selectionMode ? (
                <div className="flex items-center space-x-3">
                  {filteredThreads.length > 0 && (
                    <button
                      onClick={selectAllThreads}
                      className="flex items-center space-x-1 text-xs text-brand-primary hover:text-brand-secondary natural-light:text-natural-primary natural-dark:text-natural-dark-primary natural-light:hover:text-natural-secondary natural-dark:hover:text-natural-dark-secondary px-2 py-1 border border-transparent rounded-md"
                    >
                      <HiSelector size={14} />
                      <span>{selectedThreads.length === filteredThreads.length ? 'Deselect All' : 'Select All'}</span>
                    </button>
                  )}
                  {selectedThreads.length > 0 ? (
                    <button 
                      onClick={openMultiDeleteDialog}
                      className="flex items-center space-x-1 text-xs text-white bg-red-500 hover:bg-red-600 px-2 py-1 rounded-md"
                    >
                      <HiTrash size={14} />
                      <span>Delete ({selectedThreads.length})</span>
                    </button>
                  ) : (
                    <button 
                      onClick={toggleSelectionMode}
                      className="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md"
                    >
                      <HiX size={14} />
                      <span>Cancel</span>
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={toggleSelectionMode}
                  className="flex items-center space-x-1 text-xs text-brand-primary hover:text-brand-secondary natural-light:text-natural-primary natural-dark:text-natural-dark-primary natural-light:hover:text-natural-secondary natural-dark:hover:text-natural-dark-secondary px-2 py-1 border border-brand-primary/20 natural-light:border-natural-primary/20 natural-dark:border-natural-dark-primary/30 rounded-md"
                >
                  <HiPencil size={14} />
                  <span>Edit</span>
                </button>
              )}
            </div>
            <div className="w-full">
              <div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search chats..."
                  className="focus:ring-brand-primary focus:border-brand-primary block w-full pl-3 pr-10 py-2 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                />
              </div>
            </div>
          </div>
        </div>
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50">
            {error}
          </div>
        )}

        {filteredThreads.length === 0 && threads.length > 0 ? (
          <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
            No chats found
          </div>
        ) : threads.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-500 mb-4">No chats yet</p>
              <button
                onClick={() => navigate('/app/contacts')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#075e54] hover:bg-[#128c7e] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Start a chat
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredThreads.map((thread) => (
              <div key={thread.id} className="w-full relative">
                <div
                  className={`relative rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 shadow-sm transition-transform duration-500 ${deletingThreadId === thread.id ? 'explode-out' : ''} ${
                    thread.id === window.location.pathname.split('/').pop() 
                      ? getActiveClasses()
                      : selectedThreads.includes(thread.id) 
                        ? getActiveClasses()
                        : getHoverClasses()
                  } hover:border-gray-400 dark:hover:border-gray-500 w-full transition-colors`}
                  onClick={() => {
                    if (selectionMode) {
                      toggleThreadSelection(thread.id, { stopPropagation: () => {} } as React.MouseEvent);
                    } else {
                      // Clear unread counter for this thread when clicked
                      if (unreadThreads[thread.id]) {
                        setUnreadThreads(prev => ({
                          ...prev,
                          [thread.id]: 0
                        }));
                      }
                      navigate(`/app/chat/${thread.id}`);
                    }
                  }}
                >
                  {/* Explosion overlay */}
                  {deletingThreadId === thread.id && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50">
                      <span className="text-5xl animate-explode">💥</span>
                    </div>
                  )}
                  <div className="grid grid-cols-12 items-center gap-1">
                    {/* Selection checkbox in selection mode */}
                    {selectionMode && (
                      <div 
                        className="col-span-1 flex justify-center items-center"
                        onClick={(e) => toggleThreadSelection(thread.id, e)}
                      >
                        <div className={`w-5 h-5 rounded-md flex items-center justify-center border ${
                          selectedThreads.includes(thread.id) 
                            ? 'bg-brand-primary border-brand-primary natural-light:bg-natural-primary natural-dark:bg-natural-dark-primary' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {selectedThreads.includes(thread.id) && (
                            <HiCheck className="text-white" size={14} />
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Avatar */}
                    <div className={`${selectionMode ? 'col-span-2' : 'col-span-2'} flex justify-center`}>
                      <DiceBearAvatar 
                        url={thread.otherParticipant?.avatar_url}
                        size={40}
                        className="flex-shrink-0"
                        userId={thread.participant_ids.find(id => id !== user?.id)}
                        seed={thread.otherParticipant?.avatar_seed || undefined}
                        key={`chat-list-avatar-${thread.id}-${thread.otherParticipant?.avatar_url}-${thread.otherParticipant?.avatar_seed || ''}`}
                      />
                    </div>
                    
                    {/* Chat Content */}
                    <div className={`${selectionMode ? 'col-span-8' : 'col-span-9'} flex flex-col min-w-0 pl-3`}>
                      <div className="flex justify-between items-center">
                        <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {thread.otherParticipant?.username || 'Chat'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 ml-1 whitespace-nowrap">
                          {thread.last_message_at ? formatDate(thread.last_message_at) : ''}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                        {getLastMessage(thread)}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className={`${selectionMode ? 'col-span-1' : 'col-span-1'} flex justify-center`}>
                      {!selectionMode ? (
                        <div className="flex items-center space-x-1">
                          {unreadThreads[thread.id] && unreadThreads[thread.id] > 0 && (
                            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-500 natural-light:bg-natural-toggle-active natural-dark:bg-natural-toggle-active-dark text-white text-xs font-medium">
                              {unreadThreads[thread.id] > 9 ? '9+' : unreadThreads[thread.id]}
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDeleteDialog(thread.id, e);
                            }}
                            className="p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Delete chat"
                          >
                            <HiTrash size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          {unreadThreads[thread.id] && unreadThreads[thread.id] > 0 && (
                            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-500 natural-light:bg-natural-toggle-active natural-dark:bg-natural-toggle-active-dark text-white text-xs font-medium">
                              {unreadThreads[thread.id] > 9 ? '9+' : unreadThreads[thread.id]}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {threadToDelete ? "Delete Chat" : `Delete ${selectedThreads.length} Chats`}
              </h3>
              <button
                onClick={handleCloseDeleteDialog}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <HiX size={24} />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300">
                {threadToDelete 
                  ? "Are you sure you want to delete this chat? All messages will be permanently removed and cannot be recovered."
                  : `Are you sure you want to delete ${selectedThreads.length} chats? All messages will be permanently removed and cannot be recovered.`
                }
              </p>
              
              {deleteError && (
                <div className="mt-4 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded">
                  {deleteError}
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseDeleteDialog}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteThread}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Add this CSS to your global stylesheet (e.g., index.css or App.css):
/*
.explode-out {
  animation: explodeFade 0.6s forwards;
  z-index: 10;
}
@keyframes explodeFade {
  0% { opacity: 1; transform: scale(1); }
  60% { opacity: 1; transform: scale(1.2) rotate(-8deg); }
  80% { opacity: 0.7; transform: scale(1.4) rotate(8deg); }
  100% { opacity: 0; transform: scale(0.7) rotate(-12deg); }
}
.animate-explode {
  animation: popExplode 0.5s cubic-bezier(.36,1.7,.5,.8);
}
@keyframes popExplode {
  0% { transform: scale(0.5) rotate(-10deg); opacity: 0.7; }
  60% { transform: scale(1.3) rotate(10deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 0; }
}
*/
